import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, MailOpen } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { PageHeader, StatCard, Table } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Leads() {
  const [search, setSearch] = useState("");
  const { data } = useQuery({
    queryKey: ["admin-leads", search],
    queryFn: async () => (await api.get("/admin/leads", { params: { search: search || undefined } })).data,
  });
  const { data: digest } = useQuery({
    queryKey: ["admin-digest-performance"],
    queryFn: async () => (await api.get("/admin/digest-performance")).data,
  });

  const exportCsv = () => {
    const token = localStorage.getItem("nl_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/admin/leads/export`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "newslayer_leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div data-testid="admin-leads-page">
      <PageHeader title="Leads & Audience" sub="Every captured contact, with source attribution and interests"
        actions={<Button onClick={exportCsv} variant="outline" data-testid="export-leads-button"
          className="border-white/20 bg-transparent text-white hover:bg-white/5">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 max-w-xl">
        <StatCard label="Total leads" value={data?.total ?? "—"} accent="#FFD600" />
        <StatCard label="Newsletter opt-in" value={data?.subscribers ?? "—"} accent="#10B981" />
      </div>

      <div className="mb-8 max-w-2xl rounded-xl border border-white/10 bg-[#121212] overflow-hidden" data-testid="digest-performance-card">
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <span className="font-heading font-bold text-white text-sm flex items-center gap-2">
            <MailOpen className="h-4 w-4 text-[#00E5FF]" /> Daily Brief performance
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
            {digest?.subscribers ?? "—"} subscribers
          </span>
        </div>
        {(digest?.rows || []).length === 0 ? (
          <p className="px-4 py-4 text-gray-500 text-sm" data-testid="digest-performance-empty">
            No Brief sends yet — the first morning email will appear here with its open rate.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-2">Date</th><th className="px-4 py-2">Sent</th>
                <th className="px-4 py-2">Opens</th><th className="px-4 py-2">Open rate</th>
              </tr>
            </thead>
            <tbody>
              {digest.rows.map((r: any) => (
                <tr key={r.date} className="border-t border-white/[0.05]" data-testid={`digest-row-${r.date}`}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">{r.date}</td>
                  <td className="px-4 py-2 text-white">{r.sent}</td>
                  <td className="px-4 py-2 text-gray-300">{r.openers}<span className="text-gray-600 text-xs"> ({r.total_opens} total)</span></td>
                  <td className="px-4 py-2 font-mono font-bold text-[#00E5FF]">{r.open_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email..."
        data-testid="leads-search-input" className="bg-[#1A1A1A] border-white/10 text-white max-w-xs mb-4" />

      <Table headers={["Email", "Name", "Interests", "Captures", "Latest source", "Opt-in", "First seen"]}>
        {(data?.items || []).map((l: any) => {
          const last = (l.capture_history || []).slice(-1)[0];
          return (
            <tr key={l.id} data-testid={`lead-row-${l.id}`}>
              <td className="px-4 py-2.5 text-white">{l.email}</td>
              <td className="px-4 py-2.5 text-gray-400">{l.full_name || "—"}</td>
              <td className="px-4 py-2.5 text-gray-400 text-xs max-w-[160px] truncate">{(l.interests || []).join(", ") || "—"}</td>
              <td className="px-4 py-2.5 font-mono text-gray-400">{(l.capture_history || []).length}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{last ? `${last.source_type} @ ${last.source_page || "?"}` : "—"}</td>
              <td className="px-4 py-2.5">{l.newsletter_opt_in
                ? <span className="text-green-400 font-mono text-xs">yes</span>
                : <span className="text-gray-500 font-mono text-xs">no</span>}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{timeAgo(l.created_at)}</td>
            </tr>
          );
        })}
      </Table>
      {(data?.items || []).length === 0 && (
        <p className="text-gray-500 text-sm mt-4">No leads captured yet — they'll appear as visitors submit newsletter forms.</p>
      )}
    </div>
  );
}
